import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Tag } from 'antd';
import { PlusOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { notificationController } from '@app/controllers/notificationController';
import { getProductSpecies, getProductVarieties, updateOrCreateProduct } from '@app/api/labs.api';
import { ProductSpeciesResponse, ProductVarietiesResponse } from '@app/models/labs/LabRequest';

const { Text } = Typography;

interface Category {
  value: number;
  label: string;
}

interface Species {
  id: number;
  name: string;
  categoryId?: number;
}

interface Variety {
  id: number;
  name: string;
  speciesId?: number;
}

interface ProductsTabProps {
  lab_id?: number | null;
  categoryOption: Category[];
  editable: boolean;
  savedProducts: any[];
}

type SelectionStep = 'category' | 'species' | 'varieties' | 'completed';

const ProductsTab: React.FC<ProductsTabProps> = ({ lab_id, categoryOption, editable, savedProducts }) => {
  // Core selection state
  const [currentStep, setCurrentStep] = useState<SelectionStep>('category');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSpecies, setSelectedSpecies] = useState<Species[]>([]);
  const [selectedVarieties, setSelectedVarieties] = useState<Variety[]>([]);

  const [currentSpecies, setCurrentSpecies] = useState<Species | null>(null);
  const [currentVarieties, setCurrentVarieties] = useState<Variety[]>([]);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingSpeciesId, setEditingSpeciesId] = useState<number | null>(null);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const [speciesData, setSpeciesData] = useState<Species[]>([]);
  const [varietiesData, setVarietiesData] = useState<Variety[]>([]);
  const [loadingSpecies, setLoadingSpecies] = useState<boolean>(false);
  const [loadingVarieties, setLoadingVarieties] = useState<boolean>(false);

  const [isInEditMode, setIsInEditMode] = useState<boolean>(editable);
  const [prevEditable, setPrevEditable] = useState<boolean>(editable);

  useEffect(() => {
    if (savedProducts && savedProducts.length > 0) {
      console.log('passed product data: ', savedProducts);

      // Find the category from savedProducts
      const savedCategoryId = savedProducts[0]?.categoryId;
      const savedCategory = categoryOption.find((cat) => cat.value === savedCategoryId);

      if (savedCategory) {
        setSelectedCategory(savedCategory);
        setCurrentStep('completed');

        // Load saved data with proper variety handling
        const loadSavedData = async () => {
          try {
            setLoadingSpecies(true);
            const response = (await getProductSpecies(lab_id!, savedCategoryId)) as ProductSpeciesResponse;
            const productOptions = response?.product_options || [];

            // Map saved products to species format with actual names
            const mappedSpecies = savedProducts.map((product) => {
              const speciesOption = productOptions.find((option: any) => option.value === product.productId);
              return {
                id: product.productId,
                name: speciesOption?.label || `Product ${product.productId}`,
                categoryId: product.categoryId,
              };
            });

            // For varieties, we need to use the saved variety data directly since
            // getProductVarieties doesn't return variety data but species data
            const mappedVarieties = savedProducts.flatMap((product) =>
              product.varieties.map((variety: any) => ({
                id: variety.id,
                name: variety.name, // Use the saved variety name directly
                speciesId: product.productId,
              })),
            );

            console.log('Mapped species:', mappedSpecies);
            console.log('Mapped varieties:', mappedVarieties);

            // Only set the state if it's not already set to prevent overwriting
            setSelectedSpecies((prev) => (prev.length === 0 ? mappedSpecies : prev));
            setSelectedVarieties((prev) => (prev.length === 0 ? mappedVarieties : prev));
            setIsSaved(true);
          } catch (error) {
            console.error('Error loading saved species names:', error);
            // Fallback to basic mapping if API fails
            const mappedSpecies = savedProducts.map((product) => ({
              id: product.productId,
              name: `Product ${product.productId}`,
              categoryId: product.categoryId,
            }));

            const mappedVarieties = savedProducts.flatMap((product) =>
              product.varieties.map((variety: any) => ({
                id: variety.id,
                name: variety.name, // Use saved name directly
                speciesId: product.productId,
              })),
            );

            console.log('Fallback mapped species:', mappedSpecies);
            console.log('Fallback mapped varieties:', mappedVarieties);

            // Only set the state if it's not already set to prevent overwriting
            setSelectedSpecies((prev) => (prev.length === 0 ? mappedSpecies : prev));
            setSelectedVarieties((prev) => (prev.length === 0 ? mappedVarieties : prev));
            setIsSaved(true);
          } finally {
            setLoadingSpecies(false);
          }
        };

        if (lab_id) {
          loadSavedData();
        }
      }
    }
  }, [savedProducts, categoryOption, lab_id]);

  useEffect(() => {
    if (editable && !prevEditable) {
      setIsInEditMode(true);
    }
    setPrevEditable(editable);
  }, [editable, prevEditable]);

  const fetchSpeciesData = async (categoryId: number): Promise<void> => {
    if (!lab_id) return;

    setLoadingSpecies(true);
    try {
      const response = (await getProductSpecies(lab_id, categoryId)) as ProductSpeciesResponse;
      const productOptions = response?.product_options || [];
      const categorySpecies = productOptions.map((option: any) => ({
        id: option.value,
        name: option.label,
        categoryId: categoryId,
      }));

      console.log('Transformed species:', categorySpecies);
      setSpeciesData(categorySpecies);
    } catch (error) {
      console.error('Error fetching species:', error);
      notificationController.error({ message: 'Failed to fetch species data' });
      setSpeciesData([]);
    } finally {
      setLoadingSpecies(false);
    }
  };

  const fetchVarietiesData = async (speciesId: number): Promise<void> => {
    if (!lab_id) return;

    setLoadingVarieties(true);
    try {
      const response = (await getProductVarieties(lab_id, speciesId)) as ProductVarietiesResponse;
      console.log('Raw varieties API response:', response);

      const productVarieties = response?.product_varieties || [];
      const speciesVarieties = productVarieties.map((variety: any) => ({
        id: variety.value,
        name: variety.label,
        speciesId: speciesId,
      }));

      console.log('Transformed varieties:', speciesVarieties);
      setVarietiesData(speciesVarieties);
    } catch (error) {
      console.error('Error fetching varieties:', error);
      notificationController.error({ message: 'Failed to fetch varieties data' });
      setVarietiesData([]);
    } finally {
      setLoadingVarieties(false);
    }
  };

  // Reset search when step changes
  useEffect(() => {
    setSearchTerm('');
  }, [currentStep]);

  // Save functionality
  const handleSave = async (): Promise<void> => {
    if (!isInEditMode) return;

    if (selectedCategory && selectedSpecies.length > 0 && selectedVarieties.length > 0) {
      setIsLoading(true);
      try {
        const productsData = {
          product_category_id: selectedCategory.value,
          products: selectedSpecies.map((species) => ({
            product_id: species.id,
            product_variety_ids: selectedVarieties
              .filter((variety) => variety.speciesId === species.id)
              .map((variety) => variety.id),
          })),
        };

        const response = await updateOrCreateProduct(Number(lab_id), productsData);
        setIsSaved(true);
        setIsInEditMode(false); // Disable edit mode after save
        notificationController.success({ message: 'Products updated successfully' });
      } catch (error) {
        console.error('Error saving data:', error);
        notificationController.error({ message: 'Products could not be saved' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleEdit = (): void => {
    setIsInEditMode(true);
  };

  // Filter functions
  const getFilteredData = (data: any[], searchField: string) => {
    if (!searchTerm.trim()) return data;
    return data.filter((item) => item[searchField].toLowerCase().includes(searchTerm.toLowerCase()));
  };

  const getAvailableSpecies = () => {
    const selectedSpeciesIds = new Set(selectedSpecies.map((s) => s.id));
    return speciesData.filter((species) => !selectedSpeciesIds.has(species.id));
  };

  // Event handlers
  const handleCategorySelect = async (category: Category): Promise<void> => {
    if (!isInEditMode) return; // Prevent category selection in view mode

    console.log('Category selected, lab_id:', lab_id, 'category:', category);
    setSelectedCategory(category);
    await fetchSpeciesData(category.value);
  };

  const handleSpeciesSelect = async (species: Species): Promise<void> => {
    if (!isInEditMode) return; // Prevent species selection in view mode

    setCurrentSpecies(species);
    setCurrentVarieties([]);
    await fetchVarietiesData(species.id);
  };

  const handleVarietySelect = (variety: Variety): void => {
    if (!isInEditMode) return; // Prevent variety selection in view mode

    setCurrentVarieties((prev) => {
      const exists = prev.find((v) => v.id === variety.id);
      return exists ? prev.filter((v) => v.id !== variety.id) : [...prev, variety];
    });
  };

  const handleProceed = (): void => {
    if (!editable) return; // Prevent proceeding in view mode

    if (currentStep === 'category' && selectedCategory) {
      setCurrentStep('species');
      setCurrentSpecies(null);
      setCurrentVarieties([]);
    } else if (currentStep === 'species' && currentSpecies) {
      setCurrentStep('varieties');
      setCurrentVarieties([]);
    } else if (currentStep === 'varieties' && currentVarieties.length > 0) {
      if (currentSpecies) {
        if (isEditing && editingSpeciesId) {
          // Update varieties for existing species
          setSelectedVarieties((prev) => [
            ...prev.filter((v) => v.speciesId !== currentSpecies.id),
            ...currentVarieties,
          ]);
        } else {
          // Add new species and varieties
          setSelectedSpecies((prev) => [...prev, currentSpecies]);
          setSelectedVarieties((prev) => [...prev, ...currentVarieties]);
        }
      }

      // Reset and go to completed state
      setCurrentSpecies(null);
      setCurrentVarieties([]);
      setCurrentStep('completed');
      setIsEditing(false);
      setEditingSpeciesId(null);
      setIsSaved(false);
    }
  };

  const handleCancel = (): void => {
    if (!editable) return; // Prevent canceling in view mode

    if (isEditing) {
      setIsEditing(false);
      setEditingSpeciesId(null);
      setCurrentSpecies(null);
      setCurrentVarieties([]);
      setCurrentStep('completed');
    } else {
      // Reset everything
      setCurrentStep('category');
      setSelectedCategory(null);
      setSelectedSpecies([]);
      setSelectedVarieties([]);
      setCurrentSpecies(null);
      setCurrentVarieties([]);
    }
  };

  const handleRemoveSpecies = (speciesId: number): void => {
    if (!editable) return; // Prevent removing in view mode

    setSelectedSpecies((prev) => prev.filter((s) => s.id !== speciesId));
    setSelectedVarieties((prev) => prev.filter((v) => v.speciesId !== speciesId));
    setIsSaved(false);

    setCurrentStep('completed');
  };

  const handleRemoveVariety = (varietyId: number): void => {
    if (!editable) return; // Prevent removing in view mode

    setSelectedVarieties((prev) => prev.filter((v) => v.id !== varietyId));
    setIsSaved(false);
  };

  const handleAddMoreSpecies = async (): Promise<void> => {
    if (!editable) return; // Prevent adding in view mode

    setCurrentStep('species');
    setIsEditing(true);
    setCurrentSpecies(null);
    setCurrentVarieties([]);
    if (selectedCategory) {
      await fetchSpeciesData(selectedCategory.value);
    }
  };

  const handleAddMoreVarieties = async (speciesId: number): Promise<void> => {
    if (!editable) return; // Prevent adding in view mode

    const species = selectedSpecies.find((s) => s.id === speciesId);
    if (species) {
      setCurrentStep('varieties');
      setIsEditing(true);
      setEditingSpeciesId(speciesId);
      setCurrentSpecies(species);
      setCurrentVarieties(selectedVarieties.filter((v) => v.speciesId === speciesId));
      await fetchVarietiesData(species.id);
    }
  };

  const handleRemoveAll = (): void => {
    if (!editable) return; // Prevent removing all in view mode

    setCurrentStep('category');
    setSelectedCategory(null);
    setSelectedSpecies([]);
    setSelectedVarieties([]);
    setCurrentSpecies(null);
    setCurrentVarieties([]);
    setIsEditing(false);
    setEditingSpeciesId(null);
    setIsSaved(false);
    setSpeciesData([]);
    setVarietiesData([]);
  };

  // Render functions
  const renderSearchInput = (): JSX.Element => (
    <div style={{ marginBottom: 16 }}>
      <BaseInput
        placeholder={`Search ${
          currentStep === 'category' ? 'categories' : currentStep === 'species' ? 'species' : 'varieties'
        }...`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        allowClear
        style={{ maxWidth: 300 }}
        disabled={!editable} // Disable search in view mode
      />
    </div>
  );

  const renderItemGrid = (items: any[], selectedItem: any, onSelect: (item: any) => void, nameField = 'name') => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '8px',
      }}
    >
      {items.map((item) => (
        <Card
          key={item.id || item.value}
          size="small"
          hoverable={isInEditMode} // Only hoverable in edit mode
          onClick={isInEditMode ? () => onSelect(item) : undefined} // Only clickable in edit mode
          style={{
            cursor: isInEditMode ? 'pointer' : 'default',
            backgroundColor: selectedItem?.id === item.id || selectedItem?.value === item.value ? '#fa8c16' : '#fff',
            border:
              selectedItem?.id === item.id || selectedItem?.value === item.value
                ? '1px solid #fa8c16'
                : '1px solid #d9d9d9',
            opacity: isInEditMode ? 1 : 0.7, // Slightly faded in view mode
          }}
          bodyStyle={{ padding: '8px', textAlign: 'center' }}
        >
          <Text
            style={{
              fontSize: '12px',
              color: selectedItem?.id === item.id || selectedItem?.value === item.value ? '#fff' : '#666',
            }}
          >
            {item[nameField] || item.label}
          </Text>
        </Card>
      ))}
    </div>
  );

  const renderCategorySelection = (): JSX.Element => {
    if (!categoryOption || !Array.isArray(categoryOption)) {
      return <div style={{ textAlign: 'center', padding: '20px' }}>Loading categories...</div>;
    }

    const filteredCategories = getFilteredData(categoryOption, 'label');

    return (
      <div>
        <Text style={{ marginBottom: 16, display: 'block', color: '#666' }}>
          {editable ? 'Select Main Category' : 'Main Category (View Only)'}
        </Text>
        {editable && renderSearchInput()}
        <div
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '16px',
            backgroundColor: editable ? '#fafafa' : '#f5f5f5',
          }}
        >
          {filteredCategories.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
              {searchTerm ? `No categories found matching "${searchTerm}"` : 'No categories available'}
            </div>
          ) : (
            renderItemGrid(filteredCategories, selectedCategory, handleCategorySelect, 'label')
          )}
        </div>
      </div>
    );
  };

  const renderSpeciesSelection = (): JSX.Element => {
    const availableSpecies = getAvailableSpecies();
    const filteredSpecies = getFilteredData(availableSpecies, 'name');

    return (
      <div>
        <Text style={{ marginBottom: 16, display: 'block', color: '#666' }}>
          {isEditing ? 'Add More Species (Select One)' : 'Select Product Species (Select One)'}
        </Text>
        {renderSearchInput()}
        <div
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '16px',
            backgroundColor: editable ? '#fafafa' : '#f5f5f5',
          }}
        >
          {loadingSpecies ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>Loading species...</div>
          ) : filteredSpecies.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
              {searchTerm ? `No species found matching "${searchTerm}"` : 'No species available'}
            </div>
          ) : (
            renderItemGrid(filteredSpecies, currentSpecies, handleSpeciesSelect)
          )}
        </div>
      </div>
    );
  };

  const renderVarietiesSelection = (): JSX.Element => {
    const filteredVarieties = getFilteredData(varietiesData, 'name');

    return (
      <div>
        <Text style={{ marginBottom: 16, display: 'block', color: '#666' }}>
          {isEditing && editingSpeciesId
            ? 'Edit Varieties'
            : `Select Varieties for ${currentSpecies?.name || 'Selected Species'}`}
        </Text>
        {renderSearchInput()}
        <div
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '16px',
            backgroundColor: editable ? '#fafafa' : '#f5f5f5',
          }}
        >
          {loadingVarieties ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>Loading varieties...</div>
          ) : !currentSpecies ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>No species selected</div>
          ) : filteredVarieties.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
              {searchTerm ? `No varieties found matching "${searchTerm}"` : 'No varieties available'}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              {/* Species Card */}
              <Card
                style={{
                  backgroundColor: '#d4380d',
                  color: '#fff',
                  minWidth: '120px',
                  flexShrink: 0,
                }}
                bodyStyle={{ padding: '12px', textAlign: 'center' }}
              >
                <Text strong style={{ color: '#fff', fontSize: '12px' }}>
                  {currentSpecies.name}
                </Text>
              </Card>

              {/* Varieties Grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(6, 1fr)',
                  gap: '8px',
                  flex: 1,
                }}
              >
                {filteredVarieties.map((variety) => {
                  const isSelected = currentVarieties.find((v) => v.id === variety.id);
                  return (
                    <Card
                      key={variety.id}
                      size="small"
                      hoverable={editable}
                      onClick={editable ? () => handleVarietySelect(variety) : undefined}
                      style={{
                        cursor: editable ? 'pointer' : 'default',
                        backgroundColor: isSelected ? '#ff7a45' : '#fff', // Light orange (switched from dark maroon)
                        border: isSelected ? '2px solid #ff7a45' : '1px solid #d9d9d9',
                        opacity: editable ? 1 : 0.7,
                      }}
                      bodyStyle={{ padding: '6px', textAlign: 'center' }}
                    >
                      <Text
                        style={{
                          fontSize: '10px',
                          color: isSelected ? '#fff' : '#666',
                        }}
                      >
                        {variety.name}
                      </Text>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSelectedList = (): JSX.Element => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text style={{ color: '#666' }}>{isInEditMode ? 'Selected List' : 'Selected List (View Only)'}</Text>
        {editable && currentStep === 'completed' && selectedCategory && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {isInEditMode ? (
              <>
                {selectedSpecies.length > 0 && selectedVarieties.length > 0 && (
                  <Button type="primary" size="small" onClick={handleSave} loading={isLoading}>
                    Save
                  </Button>
                )}
                <Button danger size="small" onClick={handleRemoveAll}>
                  Remove All
                </Button>
              </>
            ) : (
              <Button type="default" size="small" onClick={handleEdit}>
                Edit
              </Button>
            )}
          </div>
        )}
      </div>

      <div
        style={{
          minHeight: '200px',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          padding: '16px',
          backgroundColor: isInEditMode ? '#fafafa' : '#f5f5f5',
        }}
      >
        {/* Show selected category and species/varieties if available */}
        {selectedCategory ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Category Card */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Card
                style={{
                  backgroundColor: '#ad2102',
                  color: '#fff',
                  minWidth: '120px',
                  minHeight: '60px',
                }}
                bodyStyle={{ padding: '16px', textAlign: 'center' }}
              >
                <Text strong style={{ color: '#fff', display: 'block' }}>
                  {selectedCategory.label}
                </Text>
              </Card>
            </div>

            {/* Species and Varieties - Show if there are selected species */}
            {selectedSpecies.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedSpecies.map((species) => {
                  const speciesVarieties = selectedVarieties.filter((v) => v.speciesId === species.id);
                  return (
                    <div key={species.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      {/* Species Card with Remove */}
                      <div style={{ position: 'relative' }}>
                        <Card
                          style={{
                            backgroundColor: '#d4380d',
                            color: '#fff',
                            minWidth: '100px',
                            flexShrink: 0,
                          }}
                          bodyStyle={{ padding: '12px', textAlign: 'center' }}
                        >
                          <Text style={{ color: '#fff', fontSize: '12px' }}>{species.name}</Text>
                        </Card>
                        {isInEditMode && (
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => handleRemoveSpecies(species.id)}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              backgroundColor: '#ff4d4f',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                            }}
                          />
                        )}
                      </div>

                      {/* Varieties */}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {/* Always show varieties if they exist, regardless of editable state */}
                        {speciesVarieties.length > 0 && (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(6, 1fr)',
                              gap: '6px',
                            }}
                          >
                            {speciesVarieties.map((variety) => (
                              <div key={variety.id} style={{ position: 'relative' }}>
                                <Card
                                  size="small"
                                  style={{
                                    backgroundColor: '#ff7a45',
                                    color: '#fff',
                                    minWidth: '60px',
                                  }}
                                  bodyStyle={{ padding: '6px', textAlign: 'center' }}
                                >
                                  <Text style={{ color: '#fff', fontSize: '10px' }}>{variety.name}</Text>
                                </Card>
                                {isInEditMode && (
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={() => handleRemoveVariety(variety.id)}
                                    style={{
                                      position: 'absolute',
                                      top: '-6px',
                                      right: '-6px',
                                      color: '#ff7a45', // Light orange (switched from dark maroon)
                                      border: '1px solid #ff7a45',
                                      borderRadius: '50%',
                                      width: '16px',
                                      height: '16px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '8px',
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add More Varieties Button - Only in edit mode */}
                        {isInEditMode && (
                          <Button
                            type="dashed"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => handleAddMoreVarieties(species.id)}
                            style={{
                              alignSelf: 'flex-start',
                              marginTop: '4px',
                              fontSize: '10px',
                              height: '24px',
                            }}
                          >
                            Add More Varieties
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Add More Species Button - Only show if there are already selected species and in edit mode */}
                {isInEditMode && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={handleAddMoreSpecies}
                      style={{ fontSize: '12px' }}
                    >
                      Add More Species
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              /* Show Add More Species button when no species are selected but category is selected */
              isInEditMode && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddMoreSpecies}
                    style={{ fontSize: '12px' }}
                  >
                    Add More Species
                  </Button>
                </div>
              )
            )}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '150px',
              color: '#999',
            }}
          >
            <Text type="secondary">No items added</Text>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px', backgroundColor: '#fff' }}>
      {/* Selection Area - Only show if editable or if there are no completed selections */}
      {editable && (
        <>
          {currentStep === 'category' && renderCategorySelection()}
          {currentStep === 'species' && renderSpeciesSelection()}
          {currentStep === 'varieties' && renderVarietiesSelection()}

          {/* Action Buttons */}
          {currentStep !== 'completed' && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '24px',
              }}
            >
              <Button
                type="primary"
                onClick={handleProceed}
                disabled={
                  (currentStep === 'category' && !selectedCategory) ||
                  (currentStep === 'species' && !currentSpecies) ||
                  (currentStep === 'varieties' && currentVarieties.length === 0)
                }
              >
                {currentStep === 'varieties' ? 'Add to List' : 'Proceed'}
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </div>
          )}
        </>
      )}

      {/* Selected List - Always visible */}
      <div style={{ marginTop: editable && currentStep !== 'completed' ? '24px' : '0px' }}>{renderSelectedList()}</div>
    </div>
  );
};

export default ProductsTab;
